import {
  AtSymbolIcon, CalendarDaysIcon, NewspaperIcon,
  PhoneIcon } from '@heroicons/react/24/solid';
import { Button } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { Company } from '../../../../@types/Company';
import { ContactInteractionType } from '../../../../@types/contactInteraction';
import type { Owner } from '../../../../@types/Owner';
import { useAdsModal } from '../../../../hooks/useAdsModal';
import { useReferentSendAvailability } from '../../../../hooks/useReferentSendAvailability';
import { setContact, setShowPhone, updateOwner } from '../../../../store/DpeOwnerReducer';
import { setCurrentStep } from '../../../../store/DpeReducer';
import type { RootState } from '../../../../store/store';
import { JWTHeader } from '../../../../utils/JWTHeader';
import { generateOwnerLetterDocx } from '../../../../utils/ownerLetterDocx';
import { DJ_URL, ROUTES } from '../../../../utils/routes';
import AdsModal from '../../../common/AdsModal/AdsModal';
import MUITable from '../../../common/MUITable';
import { showToast } from '../../../common/Notification';
import Popup from '../../../common/Popup';
import CoproLocalOwned from '../../components/CoproLocalOwned';
import DpeDetailsPopup from '../../components/DpeDetailsPopup';
import { DPE_QUALIF_STEPS } from '../../components/DpeStepsQualif/dpeQualifStep';
import DpeOwnerForm from '../../components/DpeStepsQualif/DpeStep2/DpeOwnerForm.module';
import OwnerInfos from '../../components/OwnerInfos';
import { formatDpeDetailsFromOwner } from '../../utils/formatters';
import type { FirstContactTemplateType } from '../utils/emailContentGenerator';
import {
  FIRST_CONTACT_TEMPLATE,
  generateDpeEmailContent,
} from '../utils/emailContentGenerator';
import { handleContactClick as registerContactIntent } from '../utils/OwnerCommunicationService';
import CallbackRequestForm from './CallbackRequestForm';
import EmailPreview from './EmailPreview';

const headers = [
  { title: 'Propriétaire' },
  { title: 'Propriété' },
  { title: 'DPE' },
  { title: 'Premier contact' },
  {
    title: 'Actions',
    customContentClass: 'align-top py-2 leading-normal',
  },
];

interface NotContactedTableProps {
  data?: Owner[];
  onOwnerEdit: (ownerId: number) => void;
  // API Pagination props
  apiPagination?: boolean;
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
  onRefresh?: () => void;
}

const NotContactedTable: React.FC<NotContactedTableProps> = ({ 
  data = [],
  onOwnerEdit,
  apiPagination = false,
  totalCount,
  onPageChange,
  currentPage = 0,
  currentRowsPerPage = 10,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [callbackFormOwner, setCallbackFormOwner] = useState<Owner | null>(null);
  const showPhone = useSelector((state: RootState) => state.dpeOwner.showPhone);
  const [emailPreviewData, setEmailPreviewData] = useState<{
    content: string;
    subject: string;
    to: string;
    ownerId: number | null;
    canSendFromReferent: boolean;
  }>({
    content: '',
    subject: '',
    to: '',
    ownerId: null,
    canSendFromReferent: false,
  });
  const company = useSelector((state: RootState) => state.auth.company) as Company | null;
  const companyDefaultFirstContact = (company?.brandingSettings?.defaultDpeFirstContactTemplate as FirstContactTemplateType | undefined)
    ?? FIRST_CONTACT_TEMPLATE.REFERENT_ONLY;
  const [selectedTemplate, setSelectedTemplate] = useState<FirstContactTemplateType>(companyDefaultFirstContact);
  const [emailPreviewOwner, setEmailPreviewOwner] = useState<Owner | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDpeDetails, setSelectedDpeDetails] = useState({});
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { handleAdsClick, adsModalProps } = useAdsModal();
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState<string | null>(null);
  const { canOwnerSendFromReferent, getOwnerCompany, getOwnerPrivacyPolicyLink } = useReferentSendAvailability(company?.id);

  // Fetch privacy policy link on component mount
  useEffect(() => {
    const fetchPrivacyPolicyLink = async() => {
      if (!company?.siren || !jwt) {
        return;
      }

      try {
        const response = await fetch(`/api/public/dj/companies/${company.siren}/privacy-policy`, JWTHeader(jwt));
        
        if (response.ok) {
          const responseData = await response.json();
          if (responseData.link) {
            setPrivacyPolicyLink(`${DJ_URL}${responseData.link}`);
          }
        }
      } catch (error) {
        console.error('[NotContactedTable] Error fetching privacy policy link:', error);
      }
    };

    fetchPrivacyPolicyLink();
  }, [company?.siren, jwt]);

  const handleEditClick = (owner: Owner) => {
    setSelectedOwner(owner);
    setIsEditFormOpen(true);
    dispatch(setCurrentStep(DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS));
  };

  const handleEditClose = () => {
    setIsEditFormOpen(false);
    setSelectedOwner(null);
    dispatch(setCurrentStep(null));
  };

  const handlePaperClick = async(owner: Owner) => {
    if (owner.id) {
      try {
        showToast('info', 'Génération du courrier Word en cours...');
        await generateOwnerLetterDocx(owner, getOwnerCompany(owner), selectedTemplate, jwt);
        showToast('success', 'Courrier Word généré avec succès !');
        
        const contact = await registerContactIntent(owner.id, ContactInteractionType.PAPER_FOR_CONSENT, jwt);
        if (contact) {
          contact.user = {
            id: currentUser.id,
            uuid: currentUser.uuid || '',
            firstname: currentUser.firstname,
            lastname: currentUser.lastname,
            email: currentUser.email,
            lastActivityAt: new Date().toISOString(),
            createdAt: currentUser.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }
        dispatch(setContact({ contact, ownerId: owner.id }));
        
        // Refresh the UI after PDF generation
        if (onRefresh) {
          onRefresh();
        }
      } catch (error) {
        console.error('Error generating letter DOCX:', error);
        showToast('error', 'Erreur lors de la génération du courrier Word.');
      }
    }
  };

  const handleEmailClick = async(owner: Owner) => {
    if (!owner.email) {return;}

    const defaultTemplate = companyDefaultFirstContact;
    const { subject, content } = await generateDpeEmailContent(owner, getOwnerCompany(owner), getOwnerPrivacyPolicyLink(owner, privacyPolicyLink), defaultTemplate, jwt);

    setSelectedTemplate(defaultTemplate);
    setEmailPreviewOwner(owner);
    setEmailPreviewData({
      content,
      subject,
      to: owner.email,
      ownerId: owner.id,
      canSendFromReferent: canOwnerSendFromReferent(owner),
    });
    setIsEmailPreviewOpen(true);
  };

  const handleTemplateChange = async(templateType: FirstContactTemplateType) => {
    if (!emailPreviewOwner) {return;}

    setSelectedTemplate(templateType);
    const { subject, content } = await generateDpeEmailContent(emailPreviewOwner, getOwnerCompany(emailPreviewOwner), getOwnerPrivacyPolicyLink(emailPreviewOwner, privacyPolicyLink), templateType, jwt);
    setEmailPreviewData((prev) => ({
      ...prev,
      content,
      subject,
    }));
  };

  const handlePhoneClick = async(owner: Owner) => {
    if (owner.id) {
      dispatch(setShowPhone([...showPhone, owner.id]));
      const contact = await registerContactIntent(owner.id, ContactInteractionType.PHONE, jwt);
      if (contact) {
        contact.user = {
          id: currentUser.id,
          uuid: currentUser.uuid || '',
          firstname: currentUser.firstname,
          lastname: currentUser.lastname,
          email: currentUser.email,
          lastActivityAt: new Date().toISOString(),
          createdAt: currentUser.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }
      dispatch(setContact({ contact, ownerId: owner.id }));
    }
  };

  const handleEmailAction = async() => {
    if (emailPreviewData.ownerId && jwt) {
      const contact = await registerContactIntent(emailPreviewData.ownerId, ContactInteractionType.MAIL_FOR_CONSENT, jwt);
      if (contact) {
        contact.user = {
          id: currentUser.id,
          uuid: currentUser.uuid || '',
          firstname: currentUser.firstname,
          lastname: currentUser.lastname,
          email: currentUser.email,
          lastActivityAt: new Date().toISOString(),
          createdAt: currentUser.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        dispatch(setContact({ contact, ownerId: emailPreviewData.ownerId }));

        // Refresh only after the contact has been persisted and owner step updated.
        if (onRefresh) {
          onRefresh();
        }
      }
      
      // setIsEmailPreviewOpen(false);
    }
  };

  const handleSendFromReferentEmail = async() => {
    if (!emailPreviewData.ownerId || !jwt) {
      showToast('error', 'Impossible d\'envoyer le mail : propriétaire non identifié.');
      throw new Error('Missing ownerId or jwt');
    }

    const requestInit = JWTHeader(jwt);
    requestInit.method = 'POST';
    requestInit.headers['Content-Type'] = 'application/json';
    requestInit.body = JSON.stringify({
      subject: emailPreviewData.subject,
      content: emailPreviewData.content,
    });

    const response = await fetch(`/api/owners/${emailPreviewData.ownerId}/contacts/mail/send-from-referent`, requestInit);
    const responseData = await response.json().catch(() => null);

    if (!response.ok) {
      const defaultErrorMessage = 'Erreur lors de l\'envoi SMTP depuis le mail référent.';
      const apiErrorMessage = Array.isArray(responseData?.errors)
        ? responseData.errors[0]?.message
        : responseData?.message;
      const errorMessage = apiErrorMessage || defaultErrorMessage;
      showToast('error', errorMessage);
      throw new Error(errorMessage);
    }

    showToast('success', 'Email envoyé depuis le mail référent.');
  };

  const handleDetailClick = (dpeDetails) => {
    setSelectedDpeDetails(dpeDetails);
    setIsModalOpen(true);
  };

  const getFirstContactButton = (owner: Owner) => (
    <div className="flex justify-center">
      <Button
        onClick={() => handleEmailClick(owner)}
        className={`bg-rgpd-blue-light text-rgpd-white ${!owner.email && 'cursor-not-allowed opacity-50'}`}
        disabled={!owner.email}
        type="button"
      >
        <AtSymbolIcon className="mr-1 h-4 w-4" />
        Email
      </Button>
    </div>
  );

  const handleCallbackRequestClick = (owner: Owner) => {
    setCallbackFormOwner(owner);
  };

  const handleCallbackFormClose = () => {
    setCallbackFormOwner(null);
  };

  const handleCallbackFormSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const getActionButtons = (owner: Owner) => {
    const hasEmail = Boolean(owner.email?.trim());

    const courrierButton = (
      <Button
        onClick={() => handlePaperClick(owner)}
        className={`flex-shrink-0 bg-rgpd-blue-light text-rgpd-white ${hasEmail ? 'cursor-not-allowed opacity-50' : ''}`}
        disabled={hasEmail}
        type="button"
      >
        <NewspaperIcon className="mr-1 h-4 w-4" />
        Courrier
      </Button>
    );

    const phoneBlock = showPhone.includes(owner.id) ? (
      <div className="flex flex-col items-center gap-1 rounded-md bg-gray-100 p-2 text-xs font-medium text-gray-700">
        <span>{owner.phone || 'N/A'}</span>
        <button
          onClick={() => handleCallbackRequestClick(owner)}
          className="flex items-center gap-0.5 rounded-sm bg-rgpd-green p-2 text-[12px] text-white hover:bg-rgpd-green-dark"
          type="button"
        >
          <CalendarDaysIcon className="mr-1 h-3 w-3 shrink-0" />
          Enregistrer une prise de rdv
        </button>
      </div>
    ) : (
      <Button
        onClick={() => handlePhoneClick(owner)}
        className={`bg-rgpd-blue-light text-rgpd-white ${(!owner.phone || hasEmail) && 'cursor-not-allowed opacity-50'}`}
        disabled={!owner.phone || hasEmail}
        type="button"
      >
        <PhoneIcon className="mr-1 h-4 w-4" />
        Téléphone
      </Button>
    );

    if (!hasEmail) {
      return (
        <div className="flex flex-wrap items-start gap-2">
          {courrierButton}
          {phoneBlock}
        </div>
      );
    }

    return (
      <div className="max-w-sm min-w-[11rem] text-left whitespace-normal">
        <div className="flex flex-wrap items-start gap-2">
          {courrierButton}
          {phoneBlock}
        </div>
        <p className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs leading-snug text-slate-900">
          <span className="font-semibold text-slate-950">E-mail renseigné</span>
          {' — '}
          Utilisez le bouton <span className="font-semibold">E-mail</span> dans la colonne « Premier contact ».
          L'email est la seule méthode de contact permettant la traçabilité du consentement efficacement.
        </p>
      </div>
    );
  };

  const getDpeInfo = (owner: Owner) => {
    return formatDpeDetailsFromOwner(owner, handleDetailClick, handleAdsClick);
  };

  const tableData = data.map((owner) => ({
    'Propriétaire': <OwnerInfos owner={owner} showEditButton={true} onEditClick={handleEditClick} />,
    'Propriété': <CoproLocalOwned owner={owner} />,
    'DPE': getDpeInfo(owner),
    'Premier contact': getFirstContactButton(owner),
    'Actions': getActionButtons(owner),
  }));

  const handleEmailPreviewClose = () => {
    setIsEmailPreviewOpen(false);
    setEmailPreviewOwner(null);
    setSelectedTemplate(companyDefaultFirstContact);
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <div>
      <div className="transition-all duration-300">
        <MUITable
          data={tableData}
          headers={headers}
          disablePagination={false}
          apiPagination={apiPagination}
          totalCount={totalCount}
          onPageChange={onPageChange}
          currentPage={currentPage}
          currentRowsPerPage={currentRowsPerPage}
        />
      </div>

      {isEmailPreviewOpen && emailPreviewData.to && (
        <EmailPreview
          isOpen={isEmailPreviewOpen}
          onClose={handleEmailPreviewClose}
          emailContent={emailPreviewData.content}
          emailSubject={emailPreviewData.subject}
          emailTo={emailPreviewData.to}
          onCopy={handleEmailAction}
          onOpenMailApp={handleEmailAction}
          onSendFromReferent={emailPreviewData.canSendFromReferent ? handleSendFromReferentEmail : undefined}
          templateType={selectedTemplate}
          onTemplateChange={handleTemplateChange}
          defaultTemplateType={company?.brandingSettings?.defaultDpeFirstContactTemplate ?? undefined}
          onEditDefaultTemplate={() => navigate(`${ROUTES.NEW_DASHBOARD_PARAMETRAGE}?tab=contenu`)}
        />
      )}

      {isEditFormOpen && selectedOwner && (
        <Popup
          isOpen={isEditFormOpen}
          onClose={handleEditClose}
          title="Modifier les informations du propriétaire"
          width="80%"
          maxWidth="1200px"
          height="auto"
          maxHeight="90vh"
        >
          <div className="mx-auto block w-full">
            <DpeOwnerForm
              ownerIndex={0}
              owner={selectedOwner}
              updateOwnerCallback={(updatedOwner) => {
                if (updatedOwner.id) {
                  onOwnerEdit(updatedOwner.id);
                  dispatch(updateOwner({ ownerId: updatedOwner.id, updatedOwner }));
                }
                handleEditClose();
              }}
            />
          </div>
        </Popup>
      )}

      <DpeDetailsPopup
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        dpeDetails={selectedDpeDetails}
      />

      {callbackFormOwner && (
        <CallbackRequestForm
          owner={callbackFormOwner}
          isOpen={!!callbackFormOwner}
          onClose={handleCallbackFormClose}
          onSuccess={handleCallbackFormSuccess}
        />
      )}

      <AdsModal {...adsModalProps} />
    </div>
  );
};

export default NotContactedTable; 